// Public NAAC document listing for one institute (non-schools only).
// Shows a highlighted hero + a list of published documents ordered by their
// admin-assigned Order No. Objective is displayed beneath each row.
// Also exposes `useNaacDocs(slug)` to institute layouts so they can decide
// whether to render the highlighted "NAAC" nav item.

import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Award, Download, FileText, Loader2 } from "lucide-react";

// Hook — returns { docs, loading, applicable }.
// `applicable` is false when the institute is a school (endpoint 404) or the
// slug is empty. Callers can hide the NAAC nav item when docs.length === 0.
export function useNaacDocs(slug) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicable, setApplicable] = useState(true);

  useEffect(() => {
    let cancel = false;
    if (!slug) { setLoading(false); return () => {}; }
    (async () => {
      try {
        const { data } = await api.get(`/public/institutes/${slug}/naac`);
        if (cancel) return;
        setDocs(data?.documents || []);
        setApplicable(true);
      } catch (err) {
        if (cancel) return;
        setDocs([]);
        setApplicable(err?.response?.status !== 404);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  return { docs, loading, applicable };
}

export default function NaacPublicPage({
  slug,
  headingClass = "font-serif text-4xl text-slate-900",
  accentClass = "text-[#c78e42]",
  bannerClass = "bg-gradient-to-r from-[#234333] via-[#2f5b46] to-[#234333]",
}) {
  const { docs, loading } = useNaacDocs(slug);
  const [instName, setInstName] = useState("");

  useEffect(() => {
    let cancel = false;
    if (!slug) return () => {};
    api.get(`/public/institutes/${slug}/naac`).then(({ data }) => {
      if (!cancel) setInstName(data?.institute?.name || "");
    }).catch(() => {});
    return () => { cancel = true; };
  }, [slug]);

  return (
    <div data-testid="naac-public-page">
      {/* Highlighted hero */}
      <div className={`${bannerClass} text-white`}>
        <div className="max-w-5xl mx-auto px-4 py-10 md:py-14 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/25 rounded-full px-4 py-1 text-[10px] tracking-[0.3em] uppercase font-semibold">
            <Award size={13} /> Accreditation
          </div>
          <h1 className={`${headingClass} mt-4 text-white`}>NAAC Documents</h1>
          <p className="mt-3 text-white/80 text-sm md:text-base max-w-2xl mx-auto">
            National Assessment and Accreditation Council filings{instName ? ` · ${instName}` : ""}.
            All documents below are officially published and downloadable.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-10 md:py-14 px-4">
        {loading ? (
          <div className="py-16 text-center text-slate-500" data-testid="naac-loading">
            <Loader2 className="animate-spin inline" /> Loading…
          </div>
        ) : docs.length === 0 ? (
          <div className="py-16 text-center text-slate-500 border border-dashed rounded-md" data-testid="naac-empty">
            No NAAC documents published yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="naac-list">
            {docs.map((d) => (
              <div
                key={d.id}
                data-testid={`naac-doc-${d.id}`}
                className="bg-white border border-slate-200 hover:border-[#c78e42] rounded-md overflow-hidden transition-colors"
              >
                <div className="flex items-start justify-between gap-4 px-5 py-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`${accentClass} shrink-0 mt-0.5`}>
                      <FileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] tracking-widest uppercase text-slate-400 font-mono">#{String(d.order || 0).padStart(2, "0")}</span>
                        <h3 className="text-sm md:text-base font-semibold text-slate-900 truncate">{d.name}</h3>
                      </div>
                      {d.objective && (
                        <div
                          className="prose prose-sm max-w-none text-slate-600 mt-1 leading-relaxed
                            prose-headings:mt-2 prose-headings:mb-1 prose-headings:text-slate-800
                            prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0
                            prose-a:text-[#234333] prose-a:underline-offset-2 prose-strong:text-slate-800"
                          dangerouslySetInnerHTML={{ __html: d.objective }}
                        />
                      )}
                    </div>
                  </div>
                  {d.pdf_url ? (
                    <a
                      href={d.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`naac-download-${d.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs tracking-wide uppercase font-bold bg-[#234333] hover:bg-[#c78e42] text-white rounded-sm shrink-0 transition-colors"
                    >
                      <Download size={12} /> Download
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
